import {
  useCanAccess,
  useDataProvider,
  useGetOne,
  useStore,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowRightToLine,
  ArrowUp,
  BarChart3,
  Building,
  Contact as ContactIcon,
  CornerDownLeft,
  FolderKanban,
  Handshake,
  LayoutDashboard,
  LayoutGrid,
  List as ListIcon,
  ListChecks,
  Loader2,
  Mail,
  Package,
  Plus,
  Sparkles,
  StickyNote,
  Repeat,
  Target,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import type { CrmDataProvider } from "../providers/types";
import type { Deal, GlobalSearchHit } from "../types";
import { AskHeader } from "./AskHeader";
import { AskInput } from "./AskInput";
import { entityPath } from "./entityPath";
import { AskPanel } from "./AskPanel";
import { Kbd } from "./Kbd";
import {
  KjennerViDemPanel,
  type KjennerViDemTarget,
} from "./KjennerViDemPanel";
import {
  pushRecentItem,
  readRecentItems,
  type RecentItem,
} from "./recentItems";
import { ASK_ABOUT_EVENT, type AskTarget, useAskClaude } from "./useAskClaude";

// Cmd+K-kommandopalett: ekte globalsøk (PR-S2) + naviger/hurtigopprett.
// Ved 2+ tegn slås det opp i global_search-RPC-en (kunder, kontakter,
// prosjekter, oppgaver, salg, prospekter) med topptreff gruppert per type,
// à la Asana. Global, lytter på Cmd/Ctrl+K og «mimin:open-command».
//
// To moduser, à la Raycast Quick AI: `search` (standard ved hver åpning) og
// `ask`. Tab i søkefeltet bytter til ask og sender teksten som står der som
// første melding i en ny samtale; med tomt felt vises forrige samtale.
const MIN_QUERY_LENGTH = 2;
const ASK_FALLBACK_VALUE = "ask-claude";

type Mode = "search" | "ask";
const DEBOUNCE_MS = 250;

// Pipeline-visningen huskes i ra-core-storen under samme nøkkel som
// visningsbryteren på /deals bruker.
const DEALS_VIEW_STORE_KEY = "deals.viewMode";
type DealsView = "kanban" | "list";

// Globalsøket finner ikke arkiverte muligheter, og sier ikke hvilken status
// en mulighet har. Et eget søk i deals (alle statuser, også arkiverte) fyller
// inn begge deler, så Vunnet, Tapt og Arkivert kan merkes i treffene.
type DealTag = "won" | "lost" | "disqualified" | "archived";
const MAX_DEAL_HITS = 8;

const dealTag = (
  deal: Pick<Deal, "status" | "archived_at">,
): DealTag | null => {
  if (deal.archived_at) return "archived";
  if (
    deal.status === "won" ||
    deal.status === "lost" ||
    deal.status === "disqualified"
  )
    return deal.status;
  return null;
};

// Legger mulighetene fra deals-søket til treffene fra globalsøket. Lukkede og
// arkiverte først, siden globalsøket allerede finner de åpne.
const mergeDealHits = (
  hits: GlobalSearchHit[],
  deals: Pick<Deal, "id" | "name" | "status" | "archived_at">[],
): { hits: GlobalSearchHit[]; tags: Record<string, DealTag> } => {
  const tags: Record<string, DealTag> = {};
  for (const deal of deals) {
    const tag = dealTag(deal);
    if (tag) tags[String(deal.id)] = tag;
  }
  const known = new Set(
    hits.filter((h) => h.entity_type === "deal").map((h) => h.entity_id),
  );
  const room = Math.max(0, MAX_DEAL_HITS - known.size);
  const extra = deals
    .filter((deal) => !known.has(String(deal.id)))
    .sort(
      (a, b) =>
        Number(tags[String(a.id)] == null) - Number(tags[String(b.id)] == null),
    )
    .slice(0, room)
    .map<GlobalSearchHit>((deal) => ({
      entity_type: "deal",
      entity_id: String(deal.id),
      title: deal.name,
      subtitle: null,
    }));
  return { hits: [...hits, ...extra], tags };
};

// Trefftyper som også er egne poster og dermed kan logges som «nylige».
const isRecentType = (
  type: GlobalSearchHit["entity_type"],
): type is RecentItem["type"] =>
  type !== "prospect" && type !== "note" && type !== "email";

// Sider som logges som «nylige» (PR-S3): besøk på en visnings-/
// redigeringsside registreres via record-cachen (posten er allerede
// hentet av siden selv, så oppslaget her er i praksis gratis).
type TrackedPage = {
  pattern: string;
  type: RecentItem["type"];
  resource: string;
  title: (record: Record<string, unknown>) => string;
  subtitle: (record: Record<string, unknown>) => string | null;
};

const str = (value: unknown): string =>
  typeof value === "string" ? value : "";

const TRACKED_PAGES: TrackedPage[] = [
  {
    pattern: "/companies/:id/show/*",
    type: "company",
    resource: "companies",
    title: (r) => str(r.name),
    subtitle: (r) => str(r.city) || null,
  },
  {
    pattern: "/contacts/:id/show",
    type: "contact",
    resource: "contacts",
    title: (r) => `${str(r.first_name)} ${str(r.last_name)}`.trim(),
    subtitle: (r) => str(r.company_name) || null,
  },
  {
    pattern: "/projects/:id/show",
    type: "project",
    resource: "projects",
    title: (r) => str(r.name),
    subtitle: () => null,
  },
  {
    pattern: "/deals/:id/show",
    type: "deal",
    resource: "deals",
    title: (r) => str(r.name),
    subtitle: () => null,
  },
  {
    pattern: "/work_items/:id",
    type: "work_item",
    resource: "work_items",
    title: (r) => str(r.title),
    subtitle: () => null,
  },
];

// Usynlig sporing: matcher gjeldende rute mot TRACKED_PAGES og logger
// posten i nylige-listen når navnet er kjent.
const RecentItemTracker = () => {
  const location = useLocation();

  const match = useMemo(() => {
    for (const page of TRACKED_PAGES) {
      const m = matchPath(page.pattern, location.pathname);
      const id = m?.params?.id;
      // Kun numeriske id-er – utelukker f.eks. /work_items/create.
      if (id && /^\d+$/.test(id)) return { page, id };
    }
    return null;
  }, [location.pathname]);

  const { data } = useGetOne(
    match?.page.resource ?? "companies",
    { id: match?.id ?? 0 },
    { enabled: match != null },
  );

  useEffect(() => {
    if (!match || !data) return;
    const record = data as Record<string, unknown>;
    const title = match.page.title(record);
    if (!title) return;
    pushRecentItem({
      type: match.page.type,
      id: match.id,
      title,
      subtitle: match.page.subtitle(record),
    });
  }, [match, data]);

  return null;
};

const HIT_ICONS: Record<
  GlobalSearchHit["entity_type"],
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  company: Building,
  contact: ContactIcon,
  project: FolderKanban,
  work_item: ListChecks,
  deal: Handshake,
  note: StickyNote,
  email: Mail,
  prospect: Target,
};

const GROUP_ORDER: GlobalSearchHit["entity_type"][] = [
  "company",
  "contact",
  "project",
  "work_item",
  "deal",
  "note",
  "email",
  "prospect",
];

// Raycast-aktige rader: ikonflis, tittel, dempet undertittel og type helt
// til høyre. Høyden (h-11) gir trygg klikkflate også på mobil.
const ITEM_CLASS =
  "group h-11 gap-3 rounded-lg px-2.5 data-[selected=true]:bg-accent/80";
const GROUP_CLASS =
  "p-0 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5";

const NAV_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "/": LayoutDashboard,
  "/companies": Building,
  "/projects": FolderKanban,
  "/work_items": ListChecks,
  "/contacts": ContactIcon,
  "/deals": Handshake,
  "/products": Package,
  "/prospects": Target,
  "/agreements": Repeat,
  "/reports": BarChart3,
  "/email": Mail,
};

const RowIcon = ({
  icon: Icon,
  accent = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent?: boolean;
}) => (
  <span
    aria-hidden
    className={cn(
      "grid size-7 shrink-0 place-items-center rounded-md",
      accent
        ? "bg-primary/15 text-primary"
        : "bg-muted text-muted-foreground group-data-[selected=true]:bg-background/70 group-data-[selected=true]:text-foreground",
    )}
  >
    <Icon className="size-4" />
  </span>
);

const RowText = ({
  title,
  subtitle,
  kind,
  tag,
}: {
  title: string;
  subtitle?: string | null;
  kind?: string;
  // Grå merkelapp etter tittelen, f.eks. «Vunnet» på en mulighet.
  tag?: string | null;
}) => (
  <>
    <span className="min-w-0 truncate text-sm font-medium">{title}</span>
    {tag && (
      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
        {tag}
      </span>
    )}
    {/* basis-0: undertittelen får bare plassen som er igjen, så den kortes
        ned før tittelen på smale skjermer. */}
    {subtitle && (
      <span className="min-w-0 flex-1 basis-0 truncate text-sm text-muted-foreground">
        {subtitle}
      </span>
    )}
    {kind && (
      <span className="ml-auto shrink-0 pl-3 text-xs text-muted-foreground">
        {kind}
      </span>
    )}
  </>
);

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<GlobalSearchHit[]>([]);
  const [dealTags, setDealTags] = useState<Record<string, DealTag>>({});
  const [, setDealsView] = useStore<DealsView>(DEALS_VIEW_STORE_KEY, "kanban");
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [highlighted, setHighlighted] = useState("");
  const [mode, setMode] = useState<Mode>("search");
  const [askText, setAskText] = useState("");
  const ask = useAskClaude();
  const pendingAbout = useRef<AskTarget | null>(null);
  const [aboutRequest, setAboutRequest] = useState(0);
  const navigate = useNavigate();
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  // Alle ser Rapporter (Salg-fanen); resten av fanene er bare for
  // administrator (pipeline pakke 6).
  const { canAccess: canSeeReports } = useCanAccess({
    resource: "reports",
    action: "list",
  });
  // «Avtaler» er bare for administrator.
  const { canAccess: canSeeAgreements } = useCanAccess({
    resource: "agreements",
    action: "list",
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const openFromEvent = () => setOpen(true);
    // «Spør Claude» på en post: åpne rett i chatten, med en ny samtale om
    // posten (se effekten under).
    const askAboutFromEvent = (event: Event) => {
      pendingAbout.current = (event as CustomEvent<AskTarget>).detail;
      setOpen(true);
      setAboutRequest((n) => n + 1);
    };
    document.addEventListener("keydown", down);
    window.addEventListener("mimin:open-command", openFromEvent);
    window.addEventListener(ASK_ABOUT_EVENT, askAboutFromEvent);
    return () => {
      document.removeEventListener("keydown", down);
      window.removeEventListener("mimin:open-command", openFromEvent);
      window.removeEventListener(ASK_ABOUT_EVENT, askAboutFromEvent);
    };
  }, []);

  // Nylige elementer leses når paletten åpnes, slik at listen alltid er
  // fersk (også etter navigering i andre faner).
  // Paletten starter alltid i søkemodus, også når den ble lukket med
  // Cmd+K mens den var i ask-modus.
  // Søket nullstilles også her, fordi Cmd/Ctrl+K lukker uten å gå via
  // onOpenChange; ellers ville gammel søketekst bli sendt til Claude ved Tab.
  useEffect(() => {
    if (open) {
      setRecent(readRecentItems());
      setMode("search");
      setAskText("");
      setQuery("");
      setHits([]);
      setHighlighted("");
    }
  }, [open]);

  // Kommer etter effekten over, så ask-modus vinner når paletten åpnes fra
  // «Spør Claude» på en post.
  const { startAbout } = ask;
  useEffect(() => {
    const target = pendingAbout.current;
    if (!open || !target) return;
    pendingAbout.current = null;
    startAbout(target);
    setMode("ask");
  }, [open, aboutRequest, startAbout]);

  // Debounced globalsøk. Feil svelges stille – paletten viser da bare
  // navigasjons-/opprett-snarveiene (og «Ingen treff» ved behov).
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setHits([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let active = true;
    // Deals-søket er et tillegg: feiler det, står globalsøkets treff.
    const searchDeals = async () => {
      try {
        const { data } = await dataProvider.getList<Deal>("deals", {
          filter: { q },
          pagination: { page: 1, perPage: MAX_DEAL_HITS },
          sort: { field: "updated_at", order: "DESC" },
        });
        return data ?? [];
      } catch {
        return [] as Deal[];
      }
    };
    const timer = setTimeout(() => {
      Promise.all([
        dataProvider.globalSearch(q).catch(() => [] as GlobalSearchHit[]),
        searchDeals(),
      ]).then(([found, deals]) => {
        if (!active) return;
        const merged = mergeDealHits(found, deals);
        setHits(merged.hits);
        setDealTags(merged.tags);
        setSearching(false);
      });
    }, DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, dataProvider]);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setHits([]);
      setHighlighted("");
    }
  };

  // «Kjenner vi dem?» (PR-S6): når kunde-/prospekttreff finnes, utvides
  // dialogen med et kontekstpanel for det markerte treffet.
  const hasContextHits = hits.some(
    (hit) => hit.entity_type === "company" || hit.entity_type === "prospect",
  );
  const panelTarget: KjennerViDemTarget | null = (() => {
    const companyHit = hits.find(
      (hit) =>
        hit.entity_type === "company" &&
        highlighted === `company-${hit.entity_id}`,
    );
    if (companyHit) return { type: "company", id: companyHit.entity_id };
    const prospectHit = hits.find(
      (hit) =>
        hit.entity_type === "prospect" &&
        highlighted === `prospect-${hit.entity_id}`,
    );
    if (prospectHit)
      return {
        type: "prospect",
        orgNr: prospectHit.entity_id,
        navn: prospectHit.title,
      };
    return null;
  })();

  // Tab (eller fallback-raden): bytt til ask-modus. Står det tekst i
  // søkefeltet, sendes den som første melding med en gang.
  // Med tekst starter det en ny samtale der teksten er første melding; med
  // tomt felt fortsetter forrige samtale (med mindre den er for gammel).
  const enterAsk = (text: string) => {
    setMode("ask");
    setQuery("");
    setHits([]);
    setAskText("");
    if (text.trim()) {
      ask.reset();
      ask.send(text);
    } else {
      ask.resumeOrReset();
    }
  };

  const submitAsk = (text: string) => {
    ask.send(text);
    setAskText("");
  };

  // Escape i ask-modus lukker ikke paletten, men går ett steg tilbake:
  // avbryt strømmingen, tøm feltet, og til slutt tilbake til søk.
  const onEscapeKeyDown = (event: KeyboardEvent) => {
    if (mode !== "ask") return;
    event.preventDefault();
    if (ask.status === "streaming") {
      ask.abort();
    } else if (askText) {
      setAskText("");
    } else {
      setMode("search");
    }
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Tab") return;
    // Tab skal aldri flytte fokus ut av feltet. stopPropagation trengs i
    // tillegg: dialogens fokusfelle (Radix FocusScope) flytter ellers fokus
    // til lukkeknappen ved Shift+Tab fra første element.
    event.preventDefault();
    event.stopPropagation();
    if (!event.shiftKey) enterAsk(query);
  };

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  // Velges et søketreff, logges det også som nylig (prospekter, notater
  // og e-poster er ikke egne poster i nylig-forstand og holdes utenfor).
  const goToHit = (hit: GlobalSearchHit) => {
    if (isRecentType(hit.entity_type)) {
      pushRecentItem({
        type: hit.entity_type,
        id: hit.entity_id,
        title: hit.title,
        subtitle: hit.subtitle,
      });
    }
    go(entityPath(hit.entity_type, hit.entity_id));
  };

  const allNavItems = [
    {
      label: translate("ra.page.dashboard", { _: "Oversikt" }),
      path: "/",
    },
    {
      label: translate("resources.companies.name", { smart_count: 2 }),
      path: "/companies",
    },
    {
      label: translate("resources.projects.name", { smart_count: 2 }),
      path: "/projects",
    },
    {
      label: translate("resources.work_items.name", { smart_count: 2 }),
      path: "/work_items",
    },
    {
      label: translate("resources.contacts.name", { smart_count: 2 }),
      path: "/contacts",
    },
    {
      label: translate("crm.sales.title", { _: "Pipeline" }),
      path: "/deals",
    },
    {
      label: translate("resources.products.name", { smart_count: 2 }),
      path: "/products",
    },
    {
      label: translate("crm.prospects.title", { _: "Prospekter" }),
      path: "/prospects",
    },
    {
      label: translate("crm.agreements.title", { _: "Løpende avtaler" }),
      path: "/agreements",
    },
    {
      label: translate("crm.reports.title", { _: "Rapporter" }),
      path: "/reports",
    },
    {
      label: translate("crm.email.title_page", { _: "E-post" }),
      path: "/email",
    },
  ];

  const navItems = allNavItems.filter(
    (item) =>
      (item.path !== "/reports" || canSeeReports) &&
      (item.path !== "/agreements" || canSeeAgreements),
  );

  // Pipeline som tavle eller tabell. Samme valg som bryteren på siden.
  const viewItems: { label: string; view: DealsView }[] = [
    {
      label: translate("crm.command.view_table", { _: "Vis som tabell" }),
      view: "list",
    },
    {
      label: translate("crm.command.view_board", { _: "Vis som tavle" }),
      view: "kanban",
    },
  ];

  const showDeals = (view: DealsView) => {
    setDealsView(view);
    go("/deals");
  };

  const createItems = [
    {
      label: translate("resources.projects.action.new", { _: "Nytt prosjekt" }),
      path: "/projects/create",
    },
    {
      label: translate("resources.work_items.action.new", { _: "Ny oppgave" }),
      path: "/work_items/create",
    },
    {
      label: translate("resources.companies.action.new", { _: "Ny kunde" }),
      path: "/companies/create",
    },
    {
      label: translate("resources.products.action.new", { _: "Nytt produkt" }),
      path: "/products/create",
    },
  ];

  // cmdk-filtrering er skrudd av (treffene er allerede filtrert av
  // backend), så snarveiene filtreres manuelt mot søketeksten.
  const q = query.trim().toLowerCase();
  const matchesQuery = (label: string) =>
    q.length === 0 || label.toLowerCase().includes(q);
  const matchingNavItems = navItems.filter((item) => matchesQuery(item.label));
  const visibleCreateItems = createItems.filter((item) =>
    matchesQuery(item.label),
  );
  const pipelineLabel = translate("crm.sales.title", { _: "Pipeline" });
  // Visningene vises først når man skriver, så den tomme paletten holdes
  // kort. «pipeline» finner dem også, ikke bare «tabell»/«tavle».
  const visibleViewItems =
    q.length === 0
      ? []
      : viewItems.filter(
          (item) => matchesQuery(item.label) || matchesQuery(pipelineLabel),
        );

  const groupHeading = (type: GlobalSearchHit["entity_type"]) =>
    ({
      company: translate("resources.companies.name", { smart_count: 2 }),
      contact: translate("resources.contacts.name", { smart_count: 2 }),
      project: translate("resources.projects.name", { smart_count: 2 }),
      work_item: translate("resources.work_items.name", { smart_count: 2 }),
      deal: translate("resources.deals.name", { smart_count: 2 }),
      note: translate("crm.command.notes", { _: "Notater" }),
      email: translate("crm.command.emails", { _: "E-postutsendinger" }),
      prospect: translate("crm.prospects.title", { _: "Prospekter" }),
    })[type];

  const showEmpty =
    !searching &&
    hits.length === 0 &&
    matchingNavItems.length === 0 &&
    visibleViewItems.length === 0 &&
    visibleCreateItems.length === 0;
  // Null søketreff: tilby «Spør Claude om …» som siste rad. Den ligger alltid
  // sist, så den aldri stjeler Enter fra et reelt treff.
  const showAskFallback =
    !searching && hits.length === 0 && q.length >= MIN_QUERY_LENGTH;
  // Ingen treff: vis likevel navigasjonen, så paletten aldri er en blindvei.
  // Når Claude-raden finnes, er den veien videre (og blir markert).
  const visibleNavItems =
    showEmpty && !showAskFallback ? navItems : matchingNavItems;

  // Typen til høyre i hver rad (entall), som «Application»/«Command» i Raycast.
  const kindLabel = (type: GlobalSearchHit["entity_type"]) =>
    ({
      company: translate("crm.command.kind.company", { _: "Kunde" }),
      contact: translate("crm.command.kind.contact", { _: "Kontakt" }),
      project: translate("crm.command.kind.project", { _: "Prosjekt" }),
      work_item: translate("crm.command.kind.work_item", { _: "Oppgave" }),
      deal: translate("crm.command.kind.deal", { _: "Mulighet" }),
      note: translate("crm.command.kind.note", { _: "Notat" }),
      email: translate("crm.command.kind.email", { _: "Utsending" }),
      prospect: translate("crm.command.kind.prospect", { _: "Prospekt" }),
    })[type];

  const dealTagLabel = (tag: DealTag | undefined) =>
    tag === "archived"
      ? translate("crm.command.deal_archived", { _: "Arkivert" })
      : tag
        ? translate(`crm.deals.status.${tag}`, {
            _: { won: "Vunnet", lost: "Tapt", disqualified: "Diskvalifisert" }[
              tag
            ],
          })
        : null;

  // Hva Enter gjør på den markerte raden; vises i bunnlinjen.
  const primaryAction = highlighted.startsWith("nav-")
    ? translate("crm.command.action.go", { _: "Gå til" })
    : highlighted.startsWith("create-")
      ? translate("crm.command.action.create", { _: "Opprett" })
      : highlighted.startsWith("view-")
        ? translate("crm.command.action.show", { _: "Vis" })
        : highlighted === ASK_FALLBACK_VALUE
          ? translate("crm.command.ask.hint", { _: "Spør Claude" })
          : highlighted
            ? translate("crm.command.action.open", { _: "Åpne" })
            : null;

  return (
    <>
      <RecentItemTracker />
      <CommandDialog
        open={open}
        onOpenChange={onOpenChange}
        shouldFilter={false}
        // Styrt markering: cmdk melder da også fra når den selv markerer
        // første rad, så bunnlinjen og «Kjenner vi dem?» vet hva som er valgt.
        value={highlighted}
        onValueChange={setHighlighted}
        onEscapeKeyDown={onEscapeKeyDown}
        contentClassName={
          mode === "search" && hasContextHits ? "sm:max-w-[960px]" : undefined
        }
        title={translate("crm.command.title", { _: "Kommandopalett" })}
        description={translate("crm.command.description", {
          _: "Søk etter en kommando å kjøre ...",
        })}
      >
        {mode === "ask" ? (
          <div className="flex h-[calc(100dvh-1.5rem)] flex-col duration-200 ease-out animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none sm:h-[min(620px,76vh)]">
            <AskHeader ask={ask} onBack={() => setMode("search")} />
            <AskPanel ask={ask} onNavigate={go} onExample={submitAsk} />
            <AskInput
              value={askText}
              onChange={setAskText}
              onSubmit={submitAsk}
              onStop={ask.abort}
              streaming={ask.status === "streaming"}
            />
          </div>
        ) : (
          <>
            <CommandInput
              autoFocus
              value={query}
              onValueChange={setQuery}
              onKeyDown={onSearchKeyDown}
              wrapperClassName="h-16 gap-3 border-border/60 px-5"
              iconClassName="size-5 opacity-60"
              className="h-16 text-lg"
              placeholder={translate("crm.command.placeholder", {
                _: "Søk eller naviger...",
              })}
              endAdornment={
                <span className="hidden shrink-0 items-center gap-2 text-sm text-muted-foreground sm:inline-flex">
                  <span>
                    {translate("crm.command.ask.hint", { _: "Spør Claude" })}
                  </span>
                  <Kbd>
                    <ArrowRightToLine className="size-3" />
                    <span className="sr-only">Tab</span>
                  </Kbd>
                </span>
              }
            />
            <div className="flex">
              <CommandList className="max-h-[min(420px,calc(100dvh-10rem))] flex-1 scroll-py-2 px-2 pt-1 pb-2">
                {searching && (
                  <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {translate("crm.command.searching", { _: "Søker …" })}
                  </div>
                )}
                {/* Ikke CommandEmpty: den vises bare når lista er helt tom, og
                ved null treff viser vi navigasjonen under. */}
                {showEmpty && (
                  <div className="px-3 pt-4 pb-2 text-sm text-muted-foreground">
                    {translate("crm.command.empty_for", {
                      q: query.trim(),
                      _: "Ingen treff for «%{q}»",
                    })}
                  </div>
                )}
                {q.length === 0 && recent.length > 0 && (
                  <CommandGroup
                    className={GROUP_CLASS}
                    heading={translate("crm.command.recent", { _: "Nylig" })}
                  >
                    {recent.map((item) => {
                      const Icon = HIT_ICONS[item.type];
                      return (
                        <CommandItem
                          key={`recent-${item.type}-${item.id}`}
                          value={`recent-${item.type}-${item.id}`}
                          onSelect={() => go(entityPath(item.type, item.id))}
                          className={ITEM_CLASS}
                        >
                          <RowIcon icon={Icon} />
                          <RowText
                            title={item.title}
                            subtitle={item.subtitle}
                            kind={kindLabel(item.type)}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
                {GROUP_ORDER.map((type) => {
                  const groupHits = hits.filter(
                    (hit) => hit.entity_type === type,
                  );
                  if (groupHits.length === 0) return null;
                  const Icon = HIT_ICONS[type];
                  return (
                    <CommandGroup
                      key={type}
                      className={GROUP_CLASS}
                      heading={groupHeading(type)}
                    >
                      {groupHits.map((hit) => (
                        <CommandItem
                          key={`${hit.entity_type}-${hit.entity_id}`}
                          value={`${hit.entity_type}-${hit.entity_id}`}
                          onSelect={() => goToHit(hit)}
                          className={ITEM_CLASS}
                        >
                          <RowIcon icon={Icon} />
                          <RowText
                            title={hit.title}
                            subtitle={hit.subtitle}
                            kind={kindLabel(hit.entity_type)}
                            tag={
                              hit.entity_type === "deal"
                                ? dealTagLabel(dealTags[hit.entity_id])
                                : null
                            }
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                })}
                {visibleNavItems.length > 0 && (
                  <CommandGroup
                    className={GROUP_CLASS}
                    heading={translate("crm.command.navigate", { _: "Gå til" })}
                  >
                    {visibleNavItems.map((item) => (
                      <CommandItem
                        key={item.path}
                        value={`nav-${item.path}`}
                        onSelect={() => go(item.path)}
                        className={ITEM_CLASS}
                      >
                        <RowIcon
                          icon={NAV_ICONS[item.path] ?? LayoutDashboard}
                        />
                        <RowText
                          title={item.label}
                          kind={translate("crm.command.kind.page", {
                            _: "Side",
                          })}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {visibleViewItems.length > 0 && (
                  <CommandGroup className={GROUP_CLASS} heading={pipelineLabel}>
                    {visibleViewItems.map((item) => (
                      <CommandItem
                        key={item.view}
                        value={`view-${item.view}`}
                        onSelect={() => showDeals(item.view)}
                        className={ITEM_CLASS}
                      >
                        <RowIcon
                          icon={item.view === "list" ? ListIcon : LayoutGrid}
                        />
                        <RowText
                          title={item.label}
                          kind={translate("crm.command.kind.action", {
                            _: "Handling",
                          })}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {visibleCreateItems.length > 0 && (
                  <CommandGroup
                    className={GROUP_CLASS}
                    heading={translate("crm.command.create", { _: "Opprett" })}
                  >
                    {visibleCreateItems.map((item) => (
                      <CommandItem
                        key={item.path}
                        value={`create-${item.path}`}
                        onSelect={() => go(item.path)}
                        className={ITEM_CLASS}
                      >
                        <RowIcon icon={Plus} />
                        <RowText
                          title={item.label}
                          kind={translate("crm.command.kind.action", {
                            _: "Handling",
                          })}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {showAskFallback && (
                  <CommandGroup className={cn(GROUP_CLASS, "pt-1")}>
                    <CommandItem
                      value={ASK_FALLBACK_VALUE}
                      onSelect={() => enterAsk(query)}
                      className={ITEM_CLASS}
                    >
                      <RowIcon icon={Sparkles} accent />
                      <RowText
                        title={translate("crm.command.ask.fallback", {
                          q: query.trim(),
                          _: "Spør Claude om «%{q}»",
                        })}
                        kind="Claude"
                      />
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
              {hasContextHits && (
                <aside className="hidden max-h-[min(420px,calc(100dvh-10rem))] w-72 shrink-0 border-l border-border/60 sm:block">
                  <KjennerViDemPanel target={panelTarget} />
                </aside>
              )}
            </div>
            {/* Bunnlinje à la Raycast: hva Enter gjør, og at Esc lukker. */}
            <div className="hidden h-11 shrink-0 items-center justify-between gap-3 border-t border-border/60 bg-muted/30 px-4 text-xs text-muted-foreground sm:flex">
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <ArrowUp className="size-3" />
                  <span className="sr-only">Pil opp</span>
                </Kbd>
                <Kbd>
                  <ArrowDown className="size-3" />
                  <span className="sr-only">Pil ned</span>
                </Kbd>
                <span>
                  {translate("crm.command.footer.navigate", { _: "Velg" })}
                </span>
              </span>
              <span className="flex items-center gap-3">
                {primaryAction && (
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <span>{primaryAction}</span>
                    <Kbd>
                      <CornerDownLeft className="size-3" />
                      <span className="sr-only">Enter</span>
                    </Kbd>
                  </span>
                )}
                <span aria-hidden className="h-4 w-px bg-border" />
                <span className="flex items-center gap-1.5">
                  <span>
                    {translate("crm.command.footer.close", { _: "Lukk" })}
                  </span>
                  <Kbd>Esc</Kbd>
                </span>
              </span>
            </div>
          </>
        )}
      </CommandDialog>
    </>
  );
};
